import { createFileRoute, Navigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "../components/AuthProvider.js";
import { ErrorBoundary } from "../components/ErrorBoundary.js";
import { api } from "../lib/api.js";
import { Users, Mic2, Calendar, MessageSquare, ArrowUpRight, Plus, Clock } from "lucide-react";

function DashboardWithErrorBoundary() {
  return (
    <ErrorBoundary componentName="Dashboard">
      <Dashboard />
    </ErrorBoundary>
  );
}

export const Route = createFileRoute("/")({ component: DashboardWithErrorBoundary });

function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ totalGuests: 0, totalEpisodes: 0, upcomingRecordings: 0, pendingFollowUps: 0 });

  if (!user) return <Navigate to="/login" />;

  useEffect(() => {
    api.get("/api/stats").then(r => r.json()).then(d => setStats(d.stats));
  }, []);

  const cards = [
    { label: "Total Guests", value: stats.totalGuests, icon: Users, color: "text-primary", bg: "bg-primary/10" },
    { label: "Total Episodes", value: stats.totalEpisodes, icon: Mic2, color: "text-chart-1", bg: "bg-chart-1/10" },
    { label: "Upcoming Recordings", value: stats.upcomingRecordings, icon: Calendar, color: "text-chart-3", bg: "bg-chart-3/10" },
    { label: "Pending Follow-ups", value: stats.pendingFollowUps, icon: MessageSquare, color: "text-chart-2", bg: "bg-chart-2/10" },
  ];

  return (
    <div className="space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-4xl font-black tracking-tight text-foreground">Dashboard</h1>
          <p className="text-muted-foreground text-lg">Welcome back, {user.email?.split('@')[0]}.</p>
        </div>
        <div className="flex items-center gap-3">
          <Link 
            to="/guests/new" 
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-card border border-border/50 font-bold hover:bg-muted transition-colors"
          >
            <Plus className="h-4 w-4" />
            New Guest
          </Link>
          <Link 
            to="/episodes/new"
            search={{}}
            className="flex items-center gap-2 px-5 py-2 rounded-xl bg-primary text-primary-foreground font-bold shadow-lg shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all"
          >
            <Plus className="h-4 w-4" />
            New Episode
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {cards.map((s) => (
          <div key={s.label} className="group p-6 rounded-xl border border-border/50 bg-card hover:border-primary/20 transition-all duration-300">
            <div className="flex justify-between items-start mb-4">
              <div className={`p-3 rounded-lg ${s.bg} ${s.color}`}>
                <s.icon className="h-6 w-6" />
              </div>
              <ArrowUpRight className="h-5 w-5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
            <div className="text-4xl font-black tracking-tighter text-foreground mb-1">{s.value}</div>
            <div className="text-sm font-bold uppercase tracking-wider text-muted-foreground">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        <UpcomingFollowUpsWidget />

        <div className="lg:col-span-2 p-8 rounded-xl border border-border/50 bg-card relative overflow-hidden">
          <div className="absolute top-0 right-0 p-8 opacity-5">
            <Mic2 className="h-32 w-32" />
          </div>
          <h2 className="text-2xl font-bold mb-6">Recent Activity</h2>
          <div className="space-y-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-lg bg-muted flex items-center justify-center font-black text-muted-foreground italic text-xl">
                GF
              </div>
              <div>
                <p className="font-bold">Welcome to Guestflow</p>
                <p className="text-sm text-muted-foreground">Your podcast workflow starts here. Add your first guest to begin.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="p-8 rounded-xl bg-primary text-primary-foreground shadow-xl shadow-primary/20 relative overflow-hidden">
          <div className="absolute -bottom-8 -right-8 h-48 w-48 bg-white/10 rounded-full blur-3xl" />
          <h2 className="text-xl font-bold mb-4">Pro Tip</h2>
          <p className="text-primary-foreground/80 leading-relaxed mb-6">
            Automate your post-episode workflow by setting up custom tags for guest preferences.
          </p>
          <button className="w-full py-3 rounded-lg bg-white text-primary font-black hover:bg-white/90 transition-colors">
            Learn More
          </button>
        </div>
      </div>
    </div>
  );
}

interface FollowUp {
  id: string;
  notes?: string;
  dueDate?: number | null;
  guest?: { id: string; name: string };
  // Support for guest-level follow-ups
  name?: string;
  nextFollowUpAt?: string;
}

function UpcomingFollowUpsWidget() {
  const [followUps, setFollowUps] = useState<FollowUp[]>([]);
  const [guestFollowUps, setGuestFollowUps] = useState<FollowUp[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/api/follow-ups/upcoming").then(r => r.json()),
      api.get("/api/follow-ups/guests").then(r => r.json())
    ])
      .then(([manual, guests]) => {
        setFollowUps(manual.followUps || []);
        setGuestFollowUps(guests.followUps || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const completeFollowUp = async (id: string) => {
    await api.post(`/api/follow-ups/${id}/complete`);
    setFollowUps(prev => prev.filter(f => f.id !== id));
  };

  const completeGuestFollowUp = async (guestId: string) => {
    await api.post(`/api/follow-ups/guests/${guestId}/complete`);
    setGuestFollowUps(prev => prev.filter(f => f.id !== guestId));
  };

  if (loading) return (
    <div className="p-6 rounded-xl border border-border/50 bg-card">
      <div className="flex items-center gap-3 mb-4">
        <Clock className="h-5 w-5 text-chart-2" />
        <h2 className="text-lg font-bold">Upcoming Follow-ups</h2>
      </div>
      <div className="animate-pulse space-y-3">
        <div className="h-12 bg-muted rounded-lg" />
        <div className="h-12 bg-muted rounded-lg" />
      </div>
    </div>
  );

  const totalCount = followUps.length + guestFollowUps.length;

  return (
    <div className="p-6 rounded-xl border border-border/50 bg-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Clock className="h-5 w-5 text-chart-2" />
          <h2 className="text-lg font-bold">Upcoming Follow-ups</h2>
        </div>
        <Link to="/follow-ups" className="text-sm text-primary hover:underline">
          View all
        </Link>
      </div>

      {totalCount === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <p>No upcoming follow-ups.</p>
          <p className="text-sm">You're all caught up! 🎉</p>
        </div>
      ) : (
        <div className="space-y-3 max-h-80 overflow-auto">
          {followUps.map(f => (
            <FollowUpItem 
              key={f.id} 
              followUp={f} 
              isOverdue={!!f.dueDate && f.dueDate < Date.now()}
              onComplete={() => completeFollowUp(f.id)} 
            />
          ))}
          {guestFollowUps.map(f => (
            <GuestFollowUpItem 
              key={f.id} 
              guest={f} 
              isOverdue={!!f.nextFollowUpAt && new Date(f.nextFollowUpAt).getTime() < Date.now()}
              onComplete={() => completeGuestFollowUp(f.id)} 
            />
          ))}
        </div>
      )}
    </div>
  );
}

function FollowUpItem({ followUp, isOverdue, onComplete }: { followUp: FollowUp; isOverdue?: boolean; onComplete: () => void }) {
  return (
    <div className={`flex items-start gap-3 p-3 rounded-lg ${isOverdue ? "bg-destructive/5" : "bg-muted/50"}`}>
      <button
        onClick={onComplete}
        className="mt-0.5 w-5 h-5 rounded border-2 border-muted-foreground/30 hover:border-primary hover:bg-primary/10 flex-shrink-0"
        title="Mark complete"
      />
      <div className="min-w-0 flex-1">
        <Link to="/guests/$guestId" params={{ guestId: followUp.guest?.id || "" }} className="font-medium text-sm hover:text-primary truncate block">
          {followUp.guest?.name}
        </Link>
        <p className="text-sm text-muted-foreground truncate">{followUp.notes}</p>
        {followUp.dueDate && (
          <p className={`text-xs mt-1 ${isOverdue ? "text-destructive font-medium" : "text-muted-foreground"}`}>
            {isOverdue ? "Overdue: " : "Due: "}{new Date(followUp.dueDate).toLocaleDateString()}
          </p>
        )}
      </div>
    </div>
  );
}

function GuestFollowUpItem({ guest, isOverdue, onComplete }: { guest: FollowUp; isOverdue?: boolean; onComplete: () => void }) {
  return (
    <div className={`flex items-start gap-3 p-3 rounded-lg ${isOverdue ? "bg-destructive/5" : "bg-muted/50"}`}>
      <button
        onClick={onComplete}
        className="mt-0.5 w-5 h-5 rounded border-2 border-muted-foreground/30 hover:border-primary hover:bg-primary/10 flex-shrink-0"
        title="Mark as contacted"
      />
      <div className="min-w-0 flex-1">
        <Link to="/guests/$guestId" params={{ guestId: guest.id }} className="font-medium text-sm hover:text-primary truncate block">
          {guest.name}
        </Link>
        <p className="text-sm text-muted-foreground truncate">Automatic post-recording follow-up</p>
        {guest.nextFollowUpAt && (
          <p className={`text-xs mt-1 ${isOverdue ? "text-destructive font-medium" : "text-muted-foreground"}`}>
            {isOverdue ? "Overdue: " : "Follow up by: "}{new Date(guest.nextFollowUpAt).toLocaleDateString()}
          </p>
        )}
      </div>
    </div>
  );
}
